<template>
  <div class="dn-frontpage">
    <dn-edition-collection
      :id="editionId"
      class="dn-frontpage-collection"
      :options="options"
    >
      <template #edition-top-prefix>
        <div
          v-if="shouldShowAd(topBanner) && !topBanner.aboveHeader"
          class="top-ad-placeholder"
        >
          <dn-ad
            :ad="topBanner"
            class="dn-grid-full top-ad-below-header"
            data-zone="top-ad"
          />
        </div>
      </template>
      <template #leftColumn>
        <div
          v-if="shouldShowAd(leftSkyscraper)"
          class="slot left-column"
        >
          <dn-ad
            :ad="leftSkyscraper"
            :class="{ [leftSkyscraper?.class]: true }"
          />
        </div>
      </template>
      <template #rightColumn>
        <div
          v-if="shouldShowAd(earpiece) || shouldShowAd(rightMediumRectangle) || shouldShowAd(rightSkyscraper)"
          class="slot right-column"
        >
          <dn-ad
            v-if="shouldShowAd(earpiece)"
            :ad="earpiece"
          />
          <dn-ad
            v-if="shouldShowAd(rightMediumRectangle)"
            :ad="rightMediumRectangle"
            :class="{ [rightMediumRectangle?.class]: true }"
          />
          <dn-ad
            v-if="shouldShowAd(rightSkyscraper)"
            :ad="rightSkyscraper"
            :class="{ [rightSkyscraper?.class]: true }"
          />
        </div>
      </template>
      <template
        v-if="shouldRenderBannerCard(topBannerCard)"
        #[getBannerCardSlotName(topBannerCard)]
      >
        <dn-ma-frame :id="topBannerCard.id" />
      </template>
      <template
        v-if="shouldRenderBannerCard(bannerBoxCard)"
        #[getBannerCardSlotName(bannerBoxCard)]
      >
        <dn-ma-frame :id="bannerBoxCard.id" />
      </template>
    </dn-edition-collection>
  </div>
</template>
<script setup lang="ts">
import useAbTest from '~/composables/useAbTest';

definePageMeta({
  alias: ['/d2', '/magasinet'],
  layout: 'default'
});

const nuxtApp = useNuxtApp();
const { name, front } = useRuntimeConfig().public;
const route = useRoute();

const frontpageBannerConfig = getBrazeConfig().bannerConfig?.frontpage;

nuxtApp.callHook('cpp:data:loaded', { type: 'frontpage', title: useRuntimeConfig().public.title });
const abTestValue = useAbTest().getValue();

const { frontpage: frontpageAdConfig } = getAdConfig();

const slotConfig = ref<Record<string, any> | null>(null);

provide('adConfig', frontpageAdConfig);
provide('bannerConfig', frontpageBannerConfig);
provide('slotConfig', slotConfig);

const topBanner = frontpageAdConfig?.static?.topBanner;
const leftSkyscraper = frontpageAdConfig?.static?.leftSkyscraper;
const earpiece = frontpageAdConfig?.static?.earpiece;
const rightMediumRectangle = frontpageAdConfig?.static?.rightMediumRectangle;
const rightSkyscraper = frontpageAdConfig?.static?.rightSkyscraper;

const topBannerCard = frontpageBannerConfig?.static?.topBanner;
const bannerBoxCard = frontpageBannerConfig?.static?.bannerBox;

const editionId = computed(() => {
  if (route.query && route.query.editionid) {
    if (route.query.editionid.split('PT').length > 1) {
      return route.query.editionid;
    }
  }
  return front.options.id;
});

const options = {
  size: front.options.size,
  publication: name,
  layout: front.options.layout,
  id: editionId.value
};

if (abTestValue) {
  options.abtest = abTestValue;
}

function getBannerCardSlotName (card: any) {
  return card?.slot?.[getDeviceType()] || '';
}

provide('teaser_provider', 'articleTeaser');
provide('teaser_asset_default_ratio', useRuntimeConfig().public.teaserAssetDefaultRatio);
const requestURL = useRequestURL();
const loading = ref(false);
provide('loading', loading);
const hostName = requestURL.protocol + '//' + useRuntimeConfig().public.hostname;
const title = useRuntimeConfig().public.title;
useJsonld(() => ({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebSite',
      url: hostName,
      name: title,
      publisher: {
        '@type': 'Organization',
        name: title
      }
    }
  ]
}));
</script>

<style lang="scss">
.dn-frontpage {
  display: grid;
  grid-template: auto auto / repeat(1, 1fr);
  gap: var(--row-gap);
  inline-size: min(var(--grid-width), 100vw - var(--site-padding) * 2);
  padding-bottom: calc(var(--row-gap) * 2);
  padding-top: var(--row-gap);
  margin: 0 auto;
}

.dn-edition-collection {
  --dn-grid_column-gap: var(--row-gap);
  --dn-grid_row-gap: var(--row-gap);
  --dn-edition-collection_gap: var(--row-gap);
  --dn-collection_gap: var(--row-gap);
  --dn-collection-top_content-width: var(--front-top-content-width);
  --dn-collection--header_font-family: var(--typography-editorial-subhead-min-font-family);
  --dn-collection--header_font-weight: var(--typography-editorial-subhead-min-font-weight);
  --dn-collection--header_font-size: var(--typography-editorial-subhead-min-font-size);
  --dn-collection--header_line-height: var(--typography-editorial-subhead-min-line-height);
  --dn-job-collection--header_font-family: var(--typography-editorial-subhead-max-font-family);
  --dn-job-collection--header_font-weight: var(--typography-editorial-subhead-max-font-weight);
  --dn-job-collection--header_font-size: var(--typography-editorial-subhead-max-font-size);
  --dn-job-collection--header_line-height: var(--typography-editorial-subhead-max-line-height);
  --dn-job-collection--header_color: var(--color-teaser-standard-fg);
  --dn-job-collection--icon_color: var(--color-teaser-standard-fg);
  --dn-job-collection_fill: var(--color-job-collection, var(--color-ui-generic-bg));
  --dn-job-collection_spacing: var(--row-gap);
  --dn-group_background: var(--color-teaser-standard-bg);
  --dn-group_border-radius: var(--box-radius);
  --dn-group--header_font-family: var(--typography-editorial-subhead-min-font-family);
  --dn-group--header_font-weight: var(--typography-editorial-subhead-min-font-weight);
  --dn-group--header_font-size: var(--typography-editorial-subhead-min-font-size);
  --dn-group--header_line-height: var(--typography-editorial-subhead-min-line-height);
  --dn-group_grid-spacing: var(--row-gap);

  @media only screen and (width >= 1328px) {
    --dn-edition-collection_grid-template: var(--grid-template);
    --dn-edition-collection-top_grid-area: var(--top_grid-area);
    --dn-edition-collection-bottom_grid-area: var(--bottom_grid-area);
    --dn-edition-collection-left_grid-area: var(--left_grid-area);
    --dn-edition-collection-right_grid-area: var(--right_grid-area);
    --dn-edition-collection-ad_grid-area: 1 / 3 / 1 / 4;
  }

  .brand,
  .highlight,
  .inverted,
  .opinion,
  .brand-secondary,
  .feature {
    --dn-group_spacing: var(--row-gap);

    &:has(.layout-a:only-child, .layout-b:only-child) {
      --dn-group_spacing: 0;
    }
  }
}

.dn-edition-kilkaya-collection,
.dn-edition-common-collection,
.dn-edition-section-collection {
  &.brand,
  &.highlight,
  &.inverted,
  &.opinion,
  &.brand-secondary,
  &.feature {
    --_dn-collection_spacing: var(--row-gap);

    &:has(.layout-a\+, .layout-a, .layout-b) {
      --_dn-collection_spacing: 0;
    }
  }
}
</style>
